/**
 * Sr# suggestion helper for the part-creation modal.
 *
 * Spec §6.1: "Sr# — required, manually entered positive integer, unique
 * within the project. Not auto-assigned."
 *
 * The prior implementation set `srNumber: existingPartCount + 1` silently
 * in the submit handler, which (a) violated the spec by auto-assigning and
 * (b) could collide if a part had been deleted leaving a gap below the
 * count (e.g., parts 1, 3, 4 — count is 3, suggestion is 4, collision).
 *
 * After A5 the modal renders an input field with this helper's output as
 * the *suggested* default. The user can override; the server enforces the
 * uniqueness rule and returns a friendly ConflictError if they pick a
 * value that collides.
 *
 * Algorithm: max(existing srNumbers) + 1, or 1 if none exist. Gaps below
 * the max are NOT filled by default — most teams want sequential numbering
 * even when intermediate parts have been deleted. The user can still type
 * a lower number to fill a gap if they want.
 *
 * @param existingSrNumbers  Sr#s of live parts in the project. Nulls and
 *                           non-positive values are ignored (defence —
 *                           shouldn't happen given the schema, but a
 *                           helper that's robust to bad input is cheaper
 *                           than a debug session).
 */
export function suggestNextSrNumber(existingSrNumbers: ReadonlyArray<number | null>): number {
  let max = 0;
  for (const n of existingSrNumbers) {
    if (n !== null && Number.isFinite(n) && n > max) {
      max = n;
    }
  }
  return max + 1;
}
